import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { useImpersonation } from "@/hooks/use-impersonation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Edit3, LogOut, Loader2, ShieldAlert, Ghost } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function ImpersonationBanner() {
  const { isImpersonating, isViewOnly, isEditEnabled, isGhost, impersonatingUser, pendingEditRequestId } = useImpersonation();
  const { t } = useTranslation("common");
  const { toast } = useToast();
  const qc = useQueryClient();

  const stopMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/impersonate-stop"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      window.location.href = "/admin";
    },
    onError: () => toast({ title: t("impersonation.exitFailed"), variant: "destructive" }),
  });

  const requestEditMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/request-edit-access"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: t("impersonation.editRequested"), description: t("impersonation.waitingApprove") });
    },
    onError: (err: any) => toast({ title: t("impersonation.requestFailed"), description: err.message, variant: "destructive" }),
  });

  const revokeEditMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/revoke-edit-access"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: t("impersonation.editRevoked"), description: t("impersonation.switchedBack") });
    },
    onError: () => toast({ title: t("impersonation.revokeFailed"), variant: "destructive" }),
  });

  if (!isImpersonating) return null;

  const dealerLabel = impersonatingUser?.companyName || impersonatingUser?.username || t("impersonation.dealer");

  return (
    <div
      className="w-full flex items-center justify-between gap-3 px-4 py-2 text-sm font-medium z-50 border-b"
      style={{ background: isGhost ? "rgb(49 46 129)" : isEditEnabled ? "rgb(5 150 105)" : "rgb(217 119 6)", color: "white" }}
      data-testid="banner-impersonation"
    >
      <div className="flex items-center gap-2 min-w-0">
        {isGhost ? <Ghost className="w-4 h-4 shrink-0" /> : <ShieldAlert className="w-4 h-4 shrink-0" />}
        <span className="truncate">
          {t("impersonation.viewingAs")} <span className="font-bold">{dealerLabel}</span>
        </span>
        {isGhost && (
          <Badge
            data-testid="badge-ghost-mode"
            className="bg-indigo-100 text-indigo-800 border-indigo-300 text-[10px] px-1.5 py-0 shrink-0"
            variant="outline"
          >
            <Ghost className="w-3 h-3 mr-1" />
            {t("impersonation.ghostMode")}
          </Badge>
        )}
        {isViewOnly && !isGhost && (
          <Badge
            data-testid="badge-view-only"
            className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0 shrink-0"
            variant="outline"
          >
            <Eye className="w-3 h-3 mr-1" />
            {t("impersonation.viewOnly")}
          </Badge>
        )}
        {isEditEnabled && (
          <Badge
            data-testid="badge-edit-enabled"
            className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] px-1.5 py-0 shrink-0"
            variant="outline"
          >
            <Edit3 className="w-3 h-3 mr-1" />
            {t("impersonation.editEnabled")}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isGhost && (
          <span className="text-xs opacity-80 italic" data-testid="text-ghost-readonly">
            {t("impersonation.ghostReadOnly")}
          </span>
        )}
        {!isGhost && isViewOnly && !pendingEditRequestId && (
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white border-white/30"
            onClick={() => requestEditMutation.mutate()}
            disabled={requestEditMutation.isPending}
            data-testid="button-request-edit-access"
          >
            {requestEditMutation.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Edit3 className="w-3 h-3 mr-1" />
            )}
            {t("impersonation.requestEditAccess")}
          </Button>
        )}
        {!isGhost && isViewOnly && !!pendingEditRequestId && (
          <span className="text-xs opacity-80 italic" data-testid="text-pending-request">
            {t("impersonation.awaitingApproval")}
          </span>
        )}
        {isEditEnabled && (
          <Button
            size="sm"
            variant="secondary"
            className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white border-white/30"
            onClick={() => revokeEditMutation.mutate()}
            disabled={revokeEditMutation.isPending}
            data-testid="button-revoke-edit-access"
          >
            {revokeEditMutation.isPending ? (
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <Eye className="w-3 h-3 mr-1" />
            )}
            {t("impersonation.switchToViewOnly")}
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          className="h-7 text-xs bg-white/20 hover:bg-white/30 text-white border-white/30"
          onClick={() => stopMutation.mutate()}
          disabled={stopMutation.isPending}
          data-testid="button-exit-impersonation"
        >
          {stopMutation.isPending ? (
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          ) : (
            <LogOut className="w-3 h-3 mr-1" />
          )}
          {t("impersonation.exit")}
        </Button>
      </div>
    </div>
  );
}
